import type {
  DeleteOrgRoutingPolicyResponse,
  OrgRoutingPolicyResponse,
  UpsertOrgRoutingPolicyRequest,
} from '@nadoutong/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import { opsApiRequest } from './client.ts';

/**
 * Typed wrappers + React Query hooks for the per-org routing policy
 * surface (Epic #109 / P2.9). Gateway owns the D1 table; ops-api
 * proxies; we just add session cookie handling + cache invalidation.
 *
 * Cache key: `['routing-policy', orgId]` — scoped per org so two
 * account pages viewing different tenants never alias.
 */

const BASE_PATH = '/v1/ops/orgs';

export function routingPolicyQueryKey(orgId: string): readonly ['routing-policy', string] {
  return ['routing-policy', orgId] as const;
}

export async function getOrgRoutingPolicy(
  orgId: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<OrgRoutingPolicyResponse> {
  return opsApiRequest<OrgRoutingPolicyResponse>(
    `${BASE_PATH}/${encodeURIComponent(orgId)}/routing-policy`,
    { signal },
    fetchImpl,
  );
}

export async function upsertOrgRoutingPolicy(
  orgId: string,
  body: UpsertOrgRoutingPolicyRequest,
  fetchImpl: typeof fetch,
): Promise<OrgRoutingPolicyResponse> {
  return opsApiRequest<OrgRoutingPolicyResponse>(
    `${BASE_PATH}/${encodeURIComponent(orgId)}/routing-policy`,
    { method: 'PUT', body },
    fetchImpl,
  );
}

export async function deleteOrgRoutingPolicy(
  orgId: string,
  fetchImpl: typeof fetch,
): Promise<DeleteOrgRoutingPolicyResponse> {
  return opsApiRequest<DeleteOrgRoutingPolicyResponse>(
    `${BASE_PATH}/${encodeURIComponent(orgId)}/routing-policy`,
    { method: 'DELETE' },
    fetchImpl,
  );
}

export function useOrgRoutingPolicy(
  orgId: string | null | undefined,
): UseQueryResult<OrgRoutingPolicyResponse> {
  const { fetchImpl } = useSession();
  const enabled = typeof orgId === 'string' && orgId.length > 0;
  return useQuery({
    queryKey: routingPolicyQueryKey(orgId ?? ''),
    queryFn: ({ signal }) => {
      if (!enabled || orgId === undefined || orgId === null) {
        throw new Error('useOrgRoutingPolicy: orgId required');
      }
      return getOrgRoutingPolicy(orgId, fetchImpl, signal);
    },
    enabled,
  });
}

export function useUpsertOrgRoutingPolicy(
  orgId: string,
): UseMutationResult<OrgRoutingPolicyResponse, Error, UpsertOrgRoutingPolicyRequest> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => upsertOrgRoutingPolicy(orgId, body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: routingPolicyQueryKey(orgId) });
    },
  });
}

export function useDeleteOrgRoutingPolicy(
  orgId: string,
): UseMutationResult<DeleteOrgRoutingPolicyResponse, Error, void> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => deleteOrgRoutingPolicy(orgId, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: routingPolicyQueryKey(orgId) });
    },
  });
}
